import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Collapse,
  IconButton,
  useTheme,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenTwoToneIcon from "@mui/icons-material/FolderOpenTwoTone";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import { useUserContext } from "../Context/UserContext";
import { fetchFolderContents, getUserFoldersAndFiles } from "../api";

export const transitionDuration = "0.8s";

interface FolderNode {
  name: string;
  path: string;
  subfolders: FolderNode[];
  isLoaded?: boolean;
  accessPermission?: boolean;
}

const UserSideBar: React.FC = () => {
  const {
    updatePathAndBreadcrumb,
    setCurrentPath,
    foldersTree,
    setFoldersTree,
  } = useUserContext();
  const theme = useTheme();
  const iconColor = theme.palette.mode === "light" ? "#636363" : "#e0f6ff";
  const backgroundColor =
    theme.palette.mode === "light" ? "#e0f6ff" : "#424242";
  const selectedBackgroundColor =
    theme.palette.mode === "light" ? "#d7ecf5" : "#515151";

  const [drawerWidth, setDrawerWidth] = useState(300);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [openFolders, setOpenFolders] = useState<{ [key: string]: boolean }>(
    {}
  );
  const draggingRef = useRef(false);
  const [expandAllActive, setExpandAllActive] = useState(false);
  const [hasAnyFolderBeenOpened, setHasAnyFolderBeenOpened] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const newWidth = e.clientX;
      if (newWidth >= 300 && newWidth <= 400) {
        setDrawerWidth(newWidth);
      }
    };
    const handleMouseUp = () => {
      draggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const fetchFolders = async () => {
    try {
      const storedUser = sessionStorage.getItem("user");
      if (!storedUser) return;

      const { login } = JSON.parse(storedUser);
      const fetchedItems = await getUserFoldersAndFiles(login);
      const directories = fetchedItems.filter((item) => item.isDirectory);

      const rootFolders: FolderNode[] = directories
        .filter(
          (dir) =>
            !directories.some(
              (other) =>
                other.path !== dir.path &&
                dir.path.startsWith(other.path + "\\")
            )
        )
        .map((dir) => ({
          name:
            dir.path === ""
              ? "Área de Trabalho"
              : dir.path.split("\\").pop() || "",
          path: dir.path,
          subfolders: [],
          isLoaded: false,
        }));

      setFoldersTree(rootFolders);
    } catch (error) {
      console.error("Erro ao carregar pastas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderClick = (folder: FolderNode) => {
    const fullPath = folder.path === "Área de Trabalho" ? "" : folder.path;

    // 🔄 Reconstrói o breadcrumb a partir do novo caminho
    const buildBreadcrumb = (
      path: string
    ): { path: string; label: string }[] => {
      if (!path) return [{ path: "", label: "Área de Trabalho" }];

      const parts = path.split("\\");
      let accumulatedPath = "";
      const breadcrumb = [{ path: "", label: "Área de Trabalho" }];

      for (const part of parts) {
        accumulatedPath += (accumulatedPath ? "\\" : "") + part;
        breadcrumb.push({ path: accumulatedPath, label: part });
      }

      return breadcrumb;
    };

    // ✅ Atualiza o breadcrumb com base no novo caminho
    updatePathAndBreadcrumb(fullPath);
    setSelectedPath(fullPath);
    setCurrentPath(fullPath);

    // 🔔 Dispara evento para o User.tsx (para atualizar o conteúdo da pasta)
    document.dispatchEvent(
      new CustomEvent("sidebarFolderClicked", { detail: fullPath })
    );
  };

  const handleToggleExpand = async (folder: FolderNode) => {
    const isOpen = openFolders[folder.path] || false;

    if (!isOpen && !folder.isLoaded) {
      try {
        const storedUser = sessionStorage.getItem("user");
        const { login } = storedUser ? JSON.parse(storedUser) : { login: "" };

        const contents = await fetchFolderContents(folder.path, login);
        const subfolders = await Promise.all(
          contents
            .filter((item) => item.isDirectory)
            .map(async (sub) => {
              const children = await fetchFolderContents(sub.path, login);
              const hasChildren = children.some((item) => item.isDirectory);
              return {
                name: sub.path.split("\\").pop() || "",
                path: sub.path,
                subfolders: [],
                isLoaded: !hasChildren,
              };
            })
        );

        const updateTree = (nodes: FolderNode[]): FolderNode[] =>
          nodes.map((node) => {
            if (node.path === folder.path) {
              return { ...node, subfolders, isLoaded: true };
            }
            return { ...node, subfolders: updateTree(node.subfolders) };
          });

        setFoldersTree(updateTree(foldersTree));
      } catch (error) {
        console.error("Erro ao carregar subpastas:", error);
      }
    }

    setOpenFolders((prev) => {
      const updated = { ...prev, [folder.path]: !isOpen };

      // ✅ Ativa o botão expandir/recolher na primeira abertura manual
      if (!hasAnyFolderBeenOpened) {
        setHasAnyFolderBeenOpened(true);
      }

      // Atualiza se há alguma pasta aberta
      const anyOpen = Object.values(updated).some((val) => val === true);
      setExpandAllActive(anyOpen);

      return updated;
    });
  };

  const injectNewFolderIfExpanded = async (newFolderPath: string) => {
    const rawParentPath = newFolderPath.substring(
      0,
      newFolderPath.lastIndexOf("\\")
    );
    const parentPath = rawParentPath.replace(/\\\\/g, "\\");

    try {
      const storedUser = sessionStorage.getItem("user");
      const { login } = storedUser ? JSON.parse(storedUser) : { login: "" };

      const contents = await fetchFolderContents(parentPath, login);
      const subfolders = contents.filter((item) => item.isDirectory);

      const updatedSubfolders = await Promise.all(
        subfolders.map(async (sub) => {
          const children = await fetchFolderContents(sub.path, login);
          const hasChildren = children.some((item) => item.isDirectory);
          return {
            name: sub.path.split("\\").pop() || "",
            path: sub.path,
            subfolders: [],
            isLoaded: !hasChildren,
          };
        })
      );

      // ✅ Só atualiza se o nó pai existir na árvore
      const updateTree = (nodes: FolderNode[]): FolderNode[] => {
        return nodes.map((node) => {
          if (node.path === parentPath) {
            return { ...node, subfolders: updatedSubfolders, isLoaded: true };
          }
          return {
            ...node,
            subfolders: updateTree(node.subfolders),
          };
        });
      };

      // Só atualiza se encontrar o pai na árvore atual
      const parentExists = foldersTree.some(
        (node) =>
          node.path === parentPath ||
          node.subfolders.some((sub) => sub.path === parentPath)
      );

      if (!parentExists) {
        console.warn(
          "⚠️ Nó pai não encontrado na árvore. Forçando refresh geral."
        );
        fetchFolders(); // fallback se o pai sumiu da árvore
        return;
      }

      // 🟢 Marca o pai como expandido (deixa visual aberto)
      setOpenFolders((prev) => ({
        ...prev,
        [parentPath]: true,
      }));

      setFoldersTree(updateTree(foldersTree));
    } catch (err) {
      console.error("❌ Erro ao atualizar árvore após criação de pasta:", err);
    }
  };

  const toggleExpandAllFolders = () => {
    setExpandAllActive((prev) => {
      const nextState = !prev;

      if (!nextState) {
        // Recolher tudo
        setOpenFolders({});
      } else {
        // Expandir tudo: marca todas as pastas que estão carregadas
        const collectLoadedFolders = (
          nodes: FolderNode[],
          acc: { [key: string]: boolean }
        ) => {
          nodes.forEach((node) => {
            if (node.isLoaded || node.subfolders.length > 0) {
              acc[node.path] = true;
              collectLoadedFolders(node.subfolders, acc);
            }
          });
        };

        const expanded: { [key: string]: boolean } = {};
        collectLoadedFolders(foldersTree, expanded);
        setOpenFolders(expanded);
      }

      return nextState;
    });
  };

  const renderFolderNode = (node: FolderNode, level = 0) => {
    const isOpen = openFolders[node.path] ?? false;
    const isExpandable = !node.isLoaded || node.subfolders.length > 0;

    return (
      <React.Fragment key={node.path}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleFolderClick(node)}
            sx={{
              paddingLeft: 2 + level * 2,
              backgroundColor:
                selectedPath === node.path
                  ? selectedBackgroundColor
                  : "inherit",
              transition: `background-color ${transitionDuration}`,
              height: "32px"
            }}
          >
            {isExpandable ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(node);
                }}
                sx={{ marginRight: 1, display: "flex", alignItems: "center" }}
              >
                {isOpen ? (
                  <ExpandLess sx={{ color: iconColor }} />
                ) : (
                  <ExpandMore sx={{ color: iconColor }} />
                )}
              </Box>
            ) : (
              <Box sx={{ width: 24, marginRight: 1 }} />
            )}

            <ListItemIcon>
              {isExpandable && isOpen ? (
                <FolderOpenTwoToneIcon sx={{ color: iconColor }} />
              ) : (
                <FolderIcon sx={{ color: iconColor }} />
              )}
            </ListItemIcon>

            <ListItemText
              primary={node.path === "" ? "Área de Trabalho" : node.name}
            />
          </ListItemButton>
        </ListItem>

        {isExpandable && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.subfolders.map((sub) => renderFolderNode(sub, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    const handleRefresh = (event: Event) => {
      const custom = event as CustomEvent<string>;
      const newFolderPath = custom.detail;

      // ⚠️ Zera todas as pastas abertas e o botão de expandir tudo
      setOpenFolders({});
      setExpandAllActive(false);
      setHasAnyFolderBeenOpened(false); // impede toggle no botão até abrir de novo

      if (!newFolderPath) {
        console.warn("⚠️ Caminho da nova pasta vazio.");
        return;
      }

      const parentPath = newFolderPath.substring(
        0,
        newFolderPath.lastIndexOf("\\")
      );
      const isParentOpen = openFolders[parentPath];

      if (isParentOpen) {
        injectNewFolderIfExpanded(newFolderPath);
      } else if (selectedPath === newFolderPath) {
        updatePathAndBreadcrumb(parentPath, foldersTree);
        setSelectedPath(parentPath);
        setCurrentPath(parentPath);

        setOpenFolders((prev) => {
          const updated = {
            ...prev,
            [newFolderPath]: false,
            [parentPath]: true,
          };

          // 🔁 Recalcula se há pastas abertas para corrigir seta principal
          const anyOpen = Object.values(updated).some((val) => val === true);
          setExpandAllActive(anyOpen);

          return updated;
        });

        injectNewFolderIfExpanded(newFolderPath);
      } else {
        injectNewFolderIfExpanded(newFolderPath);
      }
    };

    window.addEventListener("refreshSidebarFolders", handleRefresh);
    return () => {
      window.removeEventListener("refreshSidebarFolders", handleRefresh);
    };
  }, [openFolders, foldersTree, selectedPath]);

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          backgroundColor,
          overflow: "hidden",
          transition: `background-color ${transitionDuration} ease-in-out`,
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Toolbar
        sx={{
          minHeight: 64,
          paddingLeft: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor,
          color: iconColor,
          transition: `background-color ${transitionDuration} ease-in-out`,
        }}
      >
        <StorageRoundedIcon
          fontSize="medium"
          sx={{ mr: 1, color: iconColor }}
        />
        <Typography sx={{ ml: 1 }}>Área de Trabalho</Typography>
      </Toolbar>
      <Divider />

      <Box
        sx={{
          cursor: "ew-resize",
          width: "5px",
          backgroundColor: "transparent",
          position: "absolute",
          top: 0,
          right: 0,
          height: "100%",
          zIndex: 1000,
        }}
        onMouseDown={handleMouseDown}
      />

      {/* SCROLLABLE CONTAINER COM "PASTAS" E LISTA */}
      <Box
        sx={{
          height: "calc(100vh - 64px)",
          overflowY: "auto",
          paddingRight: "4px",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#aaa",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888",
          },
        }}
      >
        {/* Título e botão de expandir */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 16px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            <strong>PASTAS</strong>
          </Typography>
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <Tooltip
              title={
                expandAllActive
                  ? "Recolher todas as pastas"
                  : "Expandir todas as pastas"
              }
              arrow
              placement="right"
              sx={{ marginRight: "50px" }}
            >
              <IconButton
                size="small"
                onClick={
                  hasAnyFolderBeenOpened ? toggleExpandAllFolders : undefined
                }
                disabled={!hasAnyFolderBeenOpened}
                sx={{
                  opacity: hasAnyFolderBeenOpened ? 1 : 0.4,
                  cursor: hasAnyFolderBeenOpened ? "pointer" : "default",
                }}
              >
                {expandAllActive ? (
                  <KeyboardDoubleArrowUpIcon sx={{ color: iconColor }} />
                ) : (
                  <KeyboardDoubleArrowDownIcon sx={{ color: iconColor }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Lista de Pastas */}
        <List>
          {isLoading ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            foldersTree.map((folder) => renderFolderNode(folder))
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default UserSideBar;
